import React, { useState } from 'react';
import { Button, Icon, VStack } from '@wegroup/design-system';
import LoadingSpinner from '../../../../../../../../../common/src/components/LoadingSpinner';
import AdvisoryReportTitleWithDescription from '../../../../shared/AdvisoryReportTitleWithDescription';
import { useTranslation } from 'react-i18next';
import AddInsuranceModalCoverageTable from '../insurance-form/AddInsuranceModalCoverageTable';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  CoverageFormValues,
  InsuranceDetailFormValues,
} from '../../../../../types/Form';
import AddInsuranceModalCoverageForm from './AddInsuranceModalCoverageForm';
import useAddInsuranceModalContext from '../../../hooks/useAddInsuranceModalContext';
import { PartyType } from '../../../../../../../../../../types/SMT';
import { RiskType } from '../../../../shared/InsuranceProductSearch';
import { InsurancePolicyType } from '../../../../../../../types/AdvisoryReport';
import ErrorBoundary from '../../../../../../../../../app/src/components/ErrorBoundary';

interface Props {
  mode: 'EDIT' | 'ADD';
  risk: string;
  riskId?: string;
  riskType?: RiskType;
  riskIndex: number;
  onRemove?: () => void;
  type: InsurancePolicyType;
  partyType?: PartyType;
}

const AddInsuranceModalRiskPage: React.FC<Props> = ({
  mode,
  risk,
  riskId,
  riskType,
  partyType,
  riskIndex,
  type,
}) => {
  const { t } = useTranslation();
  const { isCoverageEditOpen, setIsCoverageEditOpen, riskObjects } =
    useAddInsuranceModalContext();
  const { getValues } = useFormContext<InsuranceDetailFormValues>();
  const [coverageToEdit, setCoverageToEdit] = useState<string>();
  const {
    fields: coverages,
    update,
    append,
    remove,
  } = useFieldArray<InsuranceDetailFormValues, 'coverages'>({
    name: 'coverages',
  });
  const coverageIndexToEdit = coverages.findIndex(
    ({ id: _id }) => coverageToEdit === _id,
  );
  const coveragesByRisk = coverages.filter(
    ({ risk_object }) => risk_object === risk,
  );

  if (mode === 'EDIT' && !risk) {
    return <LoadingSpinner />;
  }

  const handleRemove = (id: string) => {
    const coverageIndexToRemove = coverages.findIndex(
      ({ id: _id }) => id === _id,
    );
    remove(coverageIndexToRemove);
  };
  const handleEdit = (id: string) => {
    setCoverageToEdit(id);
    setIsCoverageEditOpen.on();
  };

  const handleSubmit = (values: CoverageFormValues) => {
    const riskOption = riskObjects.find(({ label }) => risk === label);
    if (coverageToEdit) {
      update(coverageIndexToEdit, values);
      setCoverageToEdit(undefined);
    } else {
      append({
        ...values,
        insurance_company: getValues('insurance_company'),
        risk_object: risk,
        risk_object_id: riskOption?.value,
      });
    }
    setIsCoverageEditOpen.off();
  };

  const handleCancel = () => {
    setIsCoverageEditOpen.off();
    setCoverageToEdit(undefined);
  };

  return (
    <ErrorBoundary>
      <VStack align="stretch" spacing="4" h="100%">
        <VStack align="stretch">
          <AdvisoryReportTitleWithDescription
            title={risk}
            description={t(
              'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_COVERAGE_DESCRIPTION',
            )}
          />
        </VStack>
        <AddInsuranceModalCoverageTable
          onEdit={handleEdit}
          onRemove={handleRemove}
          coverageToEdit={coverageToEdit}
          data={coveragesByRisk}
        />
        {isCoverageEditOpen ? (
          <AddInsuranceModalCoverageForm
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            coverage={coverages[coverageIndexToEdit]}
            riskId={riskId}
            riskType={riskType}
            partyType={partyType}
            type={type}
          />
        ) : (
          <Button
            alignSelf="flex-end"
            variant="secondary"
            leftIcon={<Icon name="GeneralActionsAdd" boxSize="4" />}
            size="sm"
            onClick={setIsCoverageEditOpen.on}
          >
            {t(
              'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_ADD-COVERAGE',
            )}
          </Button>
        )}
      </VStack>
    </ErrorBoundary>
  );
};

export default AddInsuranceModalRiskPage;
